import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { CommissionSplitProfile, InsertCommissionSplitProfile } from "@shared/schema";

// Fetch all split profiles (optionally filtered by type)
export function useCommissionSplitProfiles(splitType?: "agency_agent" | "primary_secondary") {
  return useQuery<CommissionSplitProfile[]>({
    queryKey: splitType ? ["commission-split-profiles", splitType] : ["commission-split-profiles"],
    queryFn: async () => {
      const url = splitType
        ? `/api/commission-split-profiles?splitType=${splitType}`
        : `/api/commission-split-profiles`;
      const response = await fetch(url);
      if (!response.ok) throw new Error("Failed to fetch commission split profiles");
      return response.json();
    },
  });
}

// Fetch a single split profile by ID
export function useCommissionSplitProfile(id: number | undefined) {
  return useQuery<CommissionSplitProfile>({
    queryKey: ["commission-split-profile", id],
    queryFn: async () => {
      const response = await fetch(`/api/commission-split-profiles/${id}`);
      if (!response.ok) throw new Error("Failed to fetch commission split profile");
      return response.json();
    },
    enabled: !!id,
  });
}

// Create a new split profile
export function useCreateCommissionSplitProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Omit<InsertCommissionSplitProfile, "createdBy">) => {
      try {
        const response = await fetch("/api/commission-split-profiles", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(data),
        });
        if (!response.ok) {
          let errorBody: any = null;
          try {
            errorBody = await response.json();
          } catch {
            // ignore JSON parse errors
          }
          const message =
            (errorBody && (errorBody.error || errorBody.message)) ||
            "Failed to create split profile";
          throw new Error(message);
        }
        return await response.json();
      } catch (err: any) {
        console.error("Error creating commission split profile:", err);
        // Always rethrow a clean Error so UI never sees low-level TypeErrors
        throw new Error(
          err?.message && !err.message.includes("Cannot read properties of null")
            ? err.message
            : "Failed to create split profile"
        );
      }
    },
    onSuccess: (data) => {
      // Invalidate all split profile queries
      queryClient.invalidateQueries({ queryKey: ["commission-split-profiles"] });
      // Also invalidate the specific type query
      const splitType = (data as any)?.splitType;
      if (splitType) {
        queryClient.invalidateQueries({
          queryKey: ["commission-split-profiles", splitType],
        });
      }
    },
  });
}

// Update a split profile
export function useUpdateCommissionSplitProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: Partial<Omit<InsertCommissionSplitProfile, "createdBy">>;
    }) => {
      const response = await fetch(`/api/commission-split-profiles/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to update split profile");
      }
      return response.json();
    },
    onSuccess: (data) => {
      // Invalidate all split profile queries
      queryClient.invalidateQueries({ queryKey: ["commission-split-profiles"] });
      // Invalidate the specific profile query
      queryClient.invalidateQueries({ queryKey: ["commission-split-profile", data.id] });
    },
  });
}

// Delete a split profile
export function useDeleteCommissionSplitProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const response = await fetch(`/api/commission-split-profiles/${id}`, {
        method: "DELETE",
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to delete split profile");
      }
      return response.json();
    },
    onSuccess: () => {
      // Invalidate all split profile queries
      queryClient.invalidateQueries({ queryKey: ["commission-split-profiles"] });
    },
  });
}

// Fetch milestones for a split profile
export function useCommissionSplitMilestones(profileId: number | null) {
  return useQuery({
    queryKey: ["commission-split-milestones", profileId],
    queryFn: async () => {
      const response = await fetch(`/api/commission-split-profiles/${profileId}/milestones`);
      if (!response.ok) throw new Error("Failed to fetch milestones");
      return response.json();
    },
    enabled: !!profileId,
  });
}

// Save milestones for a split profile (replace all)
export function useSaveCommissionSplitMilestones() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ profileId, milestones }: { profileId: number; milestones: any[] }) => {
      const response = await fetch(`/api/commission-split-profiles/${profileId}/milestones`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(milestones),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to save milestones");
      }
      return response.json();
    },
    onSuccess: (_, { profileId }) => {
      queryClient.invalidateQueries({ queryKey: ["commission-split-milestones", profileId] });
      queryClient.invalidateQueries({ queryKey: ["commission-split-profiles"] });
    },
  });
}

// Update client account manager split assignments
export function useUpdateClientAccountManagerSplits() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      agencyAgentSplitId,
      primarySecondarySplitId,
    }: {
      id: number;
      agencyAgentSplitId?: number | null;
      primarySecondarySplitId?: number | null;
    }) => {
      const response = await fetch(`/api/client-account-managers/${id}/splits`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ agencyAgentSplitId, primarySecondarySplitId }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to update split assignments");
      }
      return response.json();
    },
    onSuccess: () => {
      // Invalidate client account managers query to refresh the data
      queryClient.invalidateQueries({ queryKey: ["/api/client-account-managers"] });
      queryClient.invalidateQueries({ queryKey: ["/api/all-client-account-managers"] });
    },
  });
}
